import {
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import React from "react";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";

import {
  MessageDirection,
  ThreadFieldsForDetailFragment,
} from "~frontend/__generated__/graphql";
import MediaAttachment from "~frontend/components/MediaAttachment";

export default function MessageDetailDialog({
  message,
  onClose,
}: {
  message: ThreadFieldsForDetailFragment["messages"][0];
  onClose: () => void;
}): ReactElement {
  return (
    <Dialog open onClose={onClose} scroll="paper">
      <DialogTitle>Message Details</DialogTitle>
      <DialogContent>
        <List>
          <ListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="Internal ID"
                  description="Message identifier label"
                />
              }
              secondary={message.id}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="Direction"
                  description="Message direction (inbound/outbound) label"
                />
              }
              secondary={message.direction}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="Status"
                  description="Message status (pending, sending, sent) label"
                />
              }
              secondary={message.status}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="Type"
                  description="Message type (MMS/SMS) label"
                />
              }
              secondary={message.messageType}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="Text"
                  description="Message text label"
                />
              }
              secondary={message.text}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="Media Attachments"
                  description="Message text label"
                />
              }
              secondary={
                message.mediaAttachments?.length
                  ? message.mediaAttachments.map(attachment => (
                      <React.Fragment key={attachment.url}>
                        <MediaAttachment
                          src={attachment.url}
                          contentType={attachment.mimeType}
                        />
                        <br />
                      </React.Fragment>
                    ))
                  : "n/a"
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="Segments"
                  description="SMS segment count label"
                />
              }
              secondary={
                // Mostly here to test pluralization
                <FormattedMessage
                  defaultMessage={`{numSegments, plural, =0 {no segments}
                    =1 {one segment}
                    other {# segments}
                  }`}
                  description="Number of segments in a message"
                  values={{ numSegments: message.segments }}
                />
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="Provider"
                  description="Messaging service provider (Bandwidth/Telnyx/Twilio) label"
                />
              }
              secondary={message.provider}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="Error Code"
                  description="Message error code label"
                />
              }
              secondary={message.providerErrorCode || "n/a"}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="Provider ID"
                  description="Message provider-generated ID label"
                />
              }
              secondary={message.providerId}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  defaultMessage="Provider Timestamp"
                  description="Message provider-given timestamp"
                />
              }
              secondary={
                message.direction === MessageDirection.Inbound
                  ? new Date(message.threadOrderTime).toLocaleString()
                  : "n/a"
              }
            />
          </ListItem>
        </List>
      </DialogContent>
    </Dialog>
  );
}
